/**
 * Login Page (DC-618, DC-905)
 *
 * Authentication page for existing users.
 * Handles email/password login with MFA challenge support.
 */

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, type MFARequired } from '../../hooks/useAuth';
import type { AuthUser } from '../../contexts/AuthContext';
import { MFAChallenge } from '../../components/auth/MFAChallenge';
import '../../styles/auth.css';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeMFALogin, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MFARequired | null>(null);

  // Get redirect URL from location state. The fallback is '/', NOT '/home':
  // ProfileLanding resolves the organization's experience profile there, so a
  // banking_use_case org opens on its use-case grid while core_dataset keeps
  // /home (D-BANK-UX-02 PM test #1 — entry is profile-selected, never hardcoded).
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    const result = await login({ email, password });

    if (result.success) {
      navigate(from, { replace: true });
    } else if (result.mfa) {
      // MFA is required - show MFA challenge
      setMfaChallenge(result.mfa);
    } else {
      setError(result.error || 'Login failed');
    }
  };

  const handleMFASuccess = (accessToken: string, user: AuthUser) => {
    completeMFALogin(accessToken, user);
    navigate(from, { replace: true });
  };

  const handleMFACancel = () => {
    setMfaChallenge(null);
    setPassword('');
    setError(null);
  };

  // Show MFA challenge if required
  if (mfaChallenge) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">DATA COMPASS</div>
            </div>
            <MFAChallenge
              mfaToken={mfaChallenge.mfaToken}
              email={mfaChallenge.email}
              onSuccess={handleMFASuccess}
              onCancel={handleMFACancel}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-text">DATA COMPASS</div>
          </div>

          <h1 className="auth-title">Sign in to your account</h1>

          {/* Error message */}
          {error && (
            <div className="auth-error">
              <span className="auth-error-icon">!</span>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Email field */}
            <div className="auth-field">
              <label htmlFor="email" className="auth-label">
                Email
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="email"
                  type="email"
                  className={`auth-input ${error ? 'error' : ''}`}
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password field */}
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">
                Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`auth-input ${error ? 'error' : ''}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="auth-button-spinner" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="auth-links">
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              Don't have an account?{' '}
              <Link to="/register" className="auth-link">
                Register your organization
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
